import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { X, ChevronRight, ChevronLeft, Check, Info, Pencil } from 'lucide-react'
import { apiClient } from '../services/api'

// -- Types --

interface InspectionEntry {
  id: string
  bill_uuid: string | null
  booking_datetime: string | null
  is_combination: boolean
  booking_uuid: string | null
  user_uuid: string | null
  name_first: string | null
  name_last: string | null
  additional_info: string | null
  account_type_name: string | null
  account_type: number | null
  address: string | null
  copayment: string | null
  distance_meters: number | null
  distance_percentage: number | null
  customer_share: string | null
  cost_center: string | null
  items: { type: string; type_id: number; amount_net: string }[] | null
}

interface AccountingEntry {
  id: string
  bill_uuid: string | null
  bill_datetime: string | null
  cost_center: string | null
  cost_center_description: string | null
  amount_net: string | null
  billing_group: string | null
  entry_type: string
  is_credited: boolean
  is_corrected: boolean
  is_invoiced: boolean
  subRows: InspectionEntry[]
}

interface BillEntriesResponse {
  bill_uuid: string
  bill_datetime: string | null
  accounting_entries: AccountingEntry[]
  organization_ids: string[]
}

interface CostCenterOption {
  id: string
  code: string
  name: string
}

interface CorrectionLine {
  cost_center_uuid: string
  cost_center_label: string
  amount_net: string
  reason: string
  copy_inspection_entry_ids: string[]
}

interface EditedInspectionData {
  customer_share: string
  distance_percentage?: number
  distance_meters?: number
  items?: { type: string; type_id?: number; amount_net: string }[]
}

interface Props {
  billUuid: string
  onClose: () => void
  onSuccess: () => void
  preInvoice?: boolean  // True = pre-invoice correction (no credits)
}

function formatCurrency(val: string | null): string {
  if (!val) return ''
  const n = parseFloat(val)
  return n.toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDateTime(isoStr: string | null): string {
  if (!isoStr) return ''
  return new Date(isoStr).toLocaleString('fi-FI', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// -- Inspection Detail Modal --

function InspectionDetailModal({ entry, onClose }: { entry: InspectionEntry; onClose: () => void }) {
  const { t } = useTranslation('billing')
  const fields = [
    { label: t('inspection.bookingDatetime'), value: formatDateTime(entry.booking_datetime) },
    { label: t('inspection.bookingUuid'), value: entry.booking_uuid },
    { label: t('inspection.lastName'), value: entry.name_last },
    { label: t('inspection.firstName'), value: entry.name_first },
    { label: t('inspection.accountType'), value: entry.account_type_name },
    { label: t('inspection.address'), value: entry.address },
    { label: t('inspection.customerShare'), value: `${formatCurrency(entry.customer_share)} \u20ac` },
    { label: t('inspection.copayment'), value: `${formatCurrency(entry.copayment)} \u20ac` },
    { label: t('inspection.distanceMeters'), value: entry.distance_meters != null ? `${(entry.distance_meters / 1000).toFixed(2)} km` : '' },
    { label: t('inspection.distancePercentage'), value: entry.distance_percentage != null ? `${entry.distance_percentage.toFixed(1)} %` : '' },
    { label: t('inspection.combination'), value: entry.is_combination ? 'K' : '' },
    { label: t('inspection.costCenter'), value: entry.cost_center },
    { label: t('inspection.additionalInfo'), value: entry.additional_info },
    { label: t('inspection.customer'), value: entry.user_uuid?.slice(0, 8) },
  ]
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">{entry.name_last} {entry.name_first}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full text-sm">
            <tbody>
              {fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="py-1 px-2 text-gray-500 w-1/3">{f.label}</td>
                  <td className="py-1 px-2">{f.value || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {entry.items && entry.items.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">{t('inspection.routeTotal')}:</p>
              <table className="w-full text-xs">
                <tbody>
                  {entry.items.map((item, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="py-0.5 px-2">{item.type}</td>
                      <td className="py-0.5 px-2 text-right font-mono">{formatCurrency(item.amount_net)} &euro;</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// -- Main Wizard --

export default function CorrectionWizard({ billUuid, onClose, onSuccess, preInvoice = false }: Props) {
  const { t } = useTranslation('billing')
  // Pre-invoice skips step 1 (no credit selection needed)
  const [step, setStep] = useState(preInvoice ? 2 : 1)

  // Step 1 state
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set())
  const [selectedInspIds, setSelectedInspIds] = useState<Set<string>>(new Set())
  const [creditAmounts, setCreditAmounts] = useState<Record<string, string>>({})
  const [reason, setReason] = useState('')

  // Step 2 state
  const [correctionMode, setCorrectionMode] = useState<'credit_only' | 'credit_and_correction'>(
    preInvoice ? 'credit_and_correction' : 'credit_only'
  )
  const [corrections, setCorrections] = useState<CorrectionLine[]>([])
  const [editedInspections, setEditedInspections] = useState<Record<string, EditedInspectionData>>({})
  const [editingInspEntry, setEditingInspEntry] = useState<InspectionEntry | null>(null)

  // Step 3 state — settlement adjustment
  const [requiresSettlement, setRequiresSettlement] = useState(false)
  const [settlementChange, setSettlementChange] = useState('')
  const [settlementComments, setSettlementComments] = useState('')

  // Inspection detail modal
  const [detailEntry, setDetailEntry] = useState<InspectionEntry | null>(null)

  // Drag-and-drop state
  const [dragOverLine, setDragOverLine] = useState<number | null>(null)

  // Cost center search (per correction line)
  const [ccSearches, setCcSearches] = useState<Record<number, string>>({})

  // Fetch bill entries
  const { data: billData, isLoading } = useQuery<BillEntriesResponse>({
    queryKey: ['billEntries', billUuid],
    queryFn: () => apiClient.get(`/billing/bill-entries/${billUuid}`),
  })

  // Fetch cost centers filtered by the bill's organization(s)
  const orgIds = billData?.organization_ids || []
  const { data: costCenters } = useQuery<CostCenterOption[]>({
    queryKey: ['costCenters', ...orgIds],
    enabled: orgIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        orgIds.map(orgId =>
          apiClient.get<CostCenterOption[]>(`/invoices/cost-centers?organization_id=${orgId}`)
        )
      )
      const seen = new Set<string>()
      const merged: CostCenterOption[] = []
      for (const list of results) {
        for (const cc of list) {
          if (!seen.has(cc.id)) { seen.add(cc.id); merged.push(cc) }
        }
      }
      return merged.sort((a, b) => a.code.localeCompare(b.code))
    },
  })

  function getFilteredCostCenters(idx: number) {
    if (!costCenters) return []
    const q = (ccSearches[idx] || '').toLowerCase()
    if (!q) return costCenters.slice(0, 20)
    return costCenters.filter(
      cc => cc.code.toLowerCase().includes(q) || cc.name.toLowerCase().includes(q)
    ).slice(0, 20)
  }

  // Pre-invoice: auto-select all entries and pre-fill correction lines from originals
  const preInvoiceReady = preInvoice && !!billData && !!costCenters
  useEffect(() => {
    if (!preInvoiceReady || !billData || !costCenters) return
    // Only include uninvoiced entries for pre-invoice corrections
    const entries = billData.accounting_entries.filter(ae => !ae.is_invoiced)
    if (entries.length === 0) return
    setSelectedEntryIds(new Set(entries.map(ae => ae.id)))
    setSelectedInspIds(new Set(entries.flatMap(ae => (ae.subRows || []).map(ie => ie.id))))

    setCorrections(entries.map(ae => {
      const cc = costCenters.find(c => c.code === ae.cost_center)
        || costCenters.find(c => ae.cost_center?.startsWith(c.code))
      return {
        cost_center_uuid: cc?.id || '',
        cost_center_label: cc ? `${cc.code} ${cc.name}` : `${ae.cost_center} ${ae.cost_center_description || ''}`.trim(),
        amount_net: ae.amount_net || '0',
        reason: '',
        copy_inspection_entry_ids: (ae.subRows || []).map(ie => ie.id),
      }
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preInvoiceReady])

  // -- Step 1: accounting row selection auto-selects/deselects inspection rows --
  function toggleAccountingEntry(aeId: string, checked: boolean) {
    const next = new Set(selectedEntryIds)
    const nextInsp = new Set(selectedInspIds)
    const ae = billData?.accounting_entries.find(e => e.id === aeId)
    if (!ae) return

    if (checked) {
      next.add(aeId)
      ae.subRows?.forEach(ie => nextInsp.add(ie.id))
    } else {
      next.delete(aeId)
      ae.subRows?.forEach(ie => nextInsp.delete(ie.id))
      // Clear custom amount
      setCreditAmounts(prev => { const n = { ...prev }; delete n[aeId]; return n })
    }
    setSelectedEntryIds(next)
    setSelectedInspIds(nextInsp)
  }

  function toggleInspectionEntry(aeId: string, ieId: string) {
    const nextInsp = new Set(selectedInspIds)
    if (nextInsp.has(ieId)) nextInsp.delete(ieId)
    else nextInsp.add(ieId)
    setSelectedInspIds(nextInsp)

    // Recalculate credit amount for this accounting entry from selected inspections
    const ae = billData?.accounting_entries.find(e => e.id === aeId)
    if (ae) {
      const selectedSubs = ae.subRows?.filter(ie => nextInsp.has(ie.id)) || []
      const allSelected = selectedSubs.length === (ae.subRows?.length || 0)
      if (allSelected) {
        // All selected — use original amount
        setCreditAmounts(prev => ({ ...prev, [aeId]: ae.amount_net || '0' }))
      } else {
        // Partial — sum of selected inspection customer_shares
        const sum = selectedSubs.reduce((s, ie) => s + parseFloat(ie.customer_share || '0'), 0)
        setCreditAmounts(prev => ({ ...prev, [aeId]: sum.toFixed(2) }))
      }

      // If no inspections selected, deselect accounting row
      if (selectedSubs.length === 0) {
        const nextEntry = new Set(selectedEntryIds)
        nextEntry.delete(aeId)
        setSelectedEntryIds(nextEntry)
      } else if (!selectedEntryIds.has(aeId)) {
        // Auto-select accounting row if an inspection is selected
        const nextEntry = new Set(selectedEntryIds)
        nextEntry.add(aeId)
        setSelectedEntryIds(nextEntry)
      }
    }
  }

  // Initialize credit amounts for newly selected entries
  useEffect(() => {
    if (!billData) return
    const amounts: Record<string, string> = {}
    billData.accounting_entries.forEach(ae => {
      if (selectedEntryIds.has(ae.id) && !creditAmounts[ae.id]) {
        amounts[ae.id] = ae.amount_net || '0'
      }
    })
    if (Object.keys(amounts).length > 0) {
      setCreditAmounts(prev => ({ ...prev, ...amounts }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntryIds, billData])

  const submitMutation = useMutation({
    mutationFn: (body: unknown) => apiClient.post('/billing/corrections', body),
    onSuccess: () => onSuccess(),
  })

  const selectedEntries = useMemo(() => {
    if (!billData) return []
    return billData.accounting_entries.filter(ae => selectedEntryIds.has(ae.id))
  }, [billData, selectedEntryIds])

  const totalCreditAmount = useMemo(() => {
    return selectedEntries.reduce((sum, ae) => {
      const amt = parseFloat(creditAmounts[ae.id] || ae.amount_net || '0')
      return sum + amt
    }, 0)
  }, [selectedEntries, creditAmounts])

  // All inspection entries from selected accounting entries (for drag-drop in step 2)
  const availableInspections = useMemo(() => {
    return selectedEntries.flatMap(ae => ae.subRows || [])
  }, [selectedEntries])

  const unassignedInspections = useMemo(() => {
    const allAssigned = new Set<string>()
    corrections.forEach(c => c.copy_inspection_entry_ids.forEach(id => allAssigned.add(id)))
    return availableInspections.filter(ie => !allAssigned.has(ie.id))
  }, [availableInspections, corrections])

  function handleSubmit() {
    const credits = selectedEntries.map(ae => ({
      accounting_entry_id: ae.id,
      amount_net: parseFloat(creditAmounts[ae.id] || ae.amount_net || '0'),
      inspection_entry_ids: (ae.subRows || [])
        .filter(ie => selectedInspIds.has(ie.id))
        .map(ie => ie.id),
    }))
    const correctionLines = correctionMode === 'credit_and_correction'
      ? corrections.map(c => {
          // Collect edited inspections for this correction line
          const lineEdits: Record<string, EditedInspectionData> = {}
          for (const iid of c.copy_inspection_entry_ids) {
            if (editedInspections[iid]) {
              lineEdits[iid] = editedInspections[iid]
            }
          }
          return {
            cost_center_uuid: c.cost_center_uuid,
            amount_net: parseFloat(c.amount_net),
            reason: c.reason,
            copy_inspection_entry_ids: c.copy_inspection_entry_ids,
            edited_inspections: lineEdits,
          }
        })
      : []
    submitMutation.mutate({
      credits,
      reason,
      corrections: correctionLines,
      settlement: requiresSettlement ? {
        requires_settlement: true,
        settlement_change: parseFloat(settlementChange || '0'),
        comments: settlementComments,
      } : null,
      pre_invoice: preInvoice,
    })
  }

  function addCorrectionLine() {
    setCorrections(prev => [...prev, {
      cost_center_uuid: '', cost_center_label: '',
      amount_net: '0', reason: '',
      copy_inspection_entry_ids: [],
    }])
  }

  function updateCorrectionLine(index: number, field: string, value: string) {
    setCorrections(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c))
  }

  function removeCorrectionLine(index: number) {
    setCorrections(prev => prev.filter((_, i) => i !== index))
  }

  function toggleInspectionOnLine(lineIdx: number, inspId: string) {
    setCorrections(prev => {
      const updated = prev.map((c, i) => {
        if (i !== lineIdx) return c
        const ids = new Set(c.copy_inspection_entry_ids)
        if (ids.has(inspId)) ids.delete(inspId)
        else ids.add(inspId)
        return { ...c, copy_inspection_entry_ids: Array.from(ids) }
      })
      // Recalc amount for the changed line
      const newIds = updated[lineIdx].copy_inspection_entry_ids
      const sum = availableInspections
        .filter(ie => newIds.includes(ie.id))
        .reduce((s, ie) => s + parseFloat(ie.customer_share || '0'), 0)
      if (sum > 0) {
        updated[lineIdx] = { ...updated[lineIdx], amount_net: sum.toFixed(2) }
      }
      return updated
    })
  }

  useEffect(() => {
    if (correctionMode === 'credit_and_correction' && corrections.length === 0) {
      addCorrectionLine()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctionMode])

  const canProceedStep1 = selectedEntryIds.size > 0 && reason.trim().length > 0
  const canProceedStep2 = preInvoice
    ? (reason.trim().length > 0 && corrections.length > 0 &&
       corrections.every(c => c.cost_center_uuid && c.reason.trim() && parseFloat(c.amount_net) > 0))
    : (correctionMode === 'credit_only' || (
       corrections.length > 0 &&
       corrections.every(c => c.cost_center_uuid && c.reason.trim() && parseFloat(c.amount_net) > 0)))

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[800px] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">
              {preInvoice ? t('correction.titlePreInvoice') : t('correction.title')}
            </h2>
            <div className="flex gap-2 mt-1">
              {(preInvoice ? [2, 3] : [1, 2, 3]).map(s => (
                <span key={s} className={`w-3 h-3 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />
              ))}
              <span className="text-xs text-gray-500 ml-1">
                {t('correction.step', {
                  current: preInvoice ? step - 1 : step,
                  total: preInvoice ? 2 : 3,
                })}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ========== STEP 1 ========== */}
          {step === 1 && billData && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {preInvoice ? t('correction.step1TitlePreInvoice') : t('correction.step1Title')}
              </p>

              <div className="text-xs text-gray-500">
                Bill: {billUuid.slice(0, 8)}..
                {billData.bill_datetime && (
                  <span className="ml-2">{new Date(billData.bill_datetime).toLocaleDateString('fi-FI')}</span>
                )}
              </div>

              {/* Accounting + inspection entries with checkboxes */}
              <div className="border rounded divide-y max-h-[45vh] overflow-y-auto">
                {billData.accounting_entries.map(ae => {
                  const aeChecked = selectedEntryIds.has(ae.id)
                  const selectedSubCount = ae.subRows?.filter(ie => selectedInspIds.has(ie.id)).length || 0
                  const totalSubCount = ae.subRows?.length || 0
                  const isPartial = aeChecked && selectedSubCount > 0 && selectedSubCount < totalSubCount
                  return (
                    <div key={ae.id}>
                      <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={aeChecked}
                          ref={el => { if (el) el.indeterminate = isPartial }}
                          onChange={(e) => toggleAccountingEntry(ae.id, e.target.checked)}
                        />
                        <span className="text-sm font-medium flex-1">
                          {ae.cost_center} {ae.cost_center_description}
                        </span>
                        <span className="text-sm font-mono">
                          {formatCurrency(ae.amount_net)} &euro;
                        </span>
                      </label>
                      {/* Inspection sub-rows with checkboxes + info button */}
                      {ae.subRows?.map(ie => {
                        const ieChecked = selectedInspIds.has(ie.id)
                        return (
                          <div key={ie.id} className="pl-8 pr-3 py-1 text-xs text-gray-600 flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="mr-1"
                              checked={ieChecked}
                              onChange={() => toggleInspectionEntry(ae.id, ie.id)}
                            />
                            <span className="flex-1">{ie.name_last} {ie.name_first}</span>
                            <span className="font-mono">{formatCurrency(ie.customer_share)} &euro;</span>
                            <span className="text-gray-400 truncate max-w-[140px] ml-1">{ie.account_type_name}</span>
                            <button
                              className="text-gray-300 hover:text-blue-500 ml-1"
                              onClick={() => setDetailEntry(ie)}
                              title={t('correction.inspectionDetails')}
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>

              {/* Credit amounts (editable per accounting entry) — hidden in preInvoice mode */}
              {!preInvoice && selectedEntries.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('correction.creditAmounts')}:</p>
                  {selectedEntries.map(ae => (
                    <div key={ae.id} className="flex items-center gap-2 text-sm">
                      <span className="flex-1">{ae.cost_center} {ae.cost_center_description}:</span>
                      <input
                        type="number"
                        step="0.01"
                        max={ae.amount_net || '0'}
                        className="w-28 border rounded px-2 py-1 text-right font-mono text-sm"
                        value={creditAmounts[ae.id] || ''}
                        onChange={(e) => setCreditAmounts(prev => ({ ...prev, [ae.id]: e.target.value }))}
                      />
                      <span className="text-xs text-gray-400">
                        (max {formatCurrency(ae.amount_net)} &euro;)
                      </span>
                    </div>
                  ))}
                  <p className="text-sm font-mono text-red-600">
                    {t('correction.totalCredit')}: -{formatCurrency(String(totalCreditAmount))} &euro;
                  </p>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('correction.reason')}:</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder={t('correction.reasonPlaceholder')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ========== STEP 2 ========== */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Credit summary — hidden in preInvoice mode */}
              {!preInvoice && (
                <div className="bg-red-50 rounded p-3">
                  <p className="text-sm font-medium text-red-800">{t('correction.creditSummary')}:</p>
                  {selectedEntries.map(ae => (
                    <div key={ae.id} className="text-sm text-red-700 flex justify-between">
                      <span>{ae.cost_center} {ae.cost_center_description}</span>
                      <span className="font-mono">-{formatCurrency(creditAmounts[ae.id])} &euro;</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Mode selection — hidden in preInvoice (always correction) */}
              {!preInvoice && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="mode" checked={correctionMode === 'credit_only'} onChange={() => setCorrectionMode('credit_only')} />
                    <span className="text-sm">{t('correction.creditOnly')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="mode" checked={correctionMode === 'credit_and_correction'} onChange={() => setCorrectionMode('credit_and_correction')} />
                    <span className="text-sm">{t('correction.creditAndCorrection')}</span>
                  </label>
                </div>
              )}

              {/* Reason field — in preInvoice mode, shown here since step 1 is skipped */}
              {preInvoice && (
                <div>
                  <label className="block text-sm font-medium mb-1">{t('correction.reason')}:</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder={t('correction.reasonPlaceholderPreInvoice')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              )}

              {/* Correction lines */}
              {correctionMode === 'credit_and_correction' && (
                <div className="space-y-3">
                  <div className="border rounded p-3 space-y-3">
                    <p className="text-sm font-medium">{t('correction.correctionLines')}:</p>
                    {corrections.map((corr, idx) => {
                      const ccSearchVal = ccSearches[idx] || ''
                      const filtered = getFilteredCostCenters(idx)
                      const droppedInspections = availableInspections.filter(
                        ie => corr.copy_inspection_entry_ids.includes(ie.id)
                      )
                      return (
                        <div
                          key={idx}
                          className={`space-y-2 pb-3 border-b last:border-0 ${
                            dragOverLine === idx ? 'bg-blue-50 ring-2 ring-blue-300 rounded' : ''
                          }`}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverLine(idx) }}
                          onDragLeave={() => setDragOverLine(null)}
                          onDrop={(e) => {
                            e.preventDefault()
                            setDragOverLine(null)
                            const inspId = e.dataTransfer.getData('text/plain')
                            if (inspId && !corr.copy_inspection_entry_ids.includes(inspId)) {
                              toggleInspectionOnLine(idx, inspId)
                            }
                          }}
                        >
                          <div className="flex gap-2 items-center">
                            <span className="text-xs text-gray-400 w-4">{idx + 1}</span>
                            <div className="relative flex-1">
                              <input
                                type="text"
                                className="w-full border rounded px-2 py-1 text-sm"
                                placeholder={t('correction.searchCostCenter')}
                                value={corr.cost_center_label || ccSearchVal}
                                onChange={(e) => {
                                  setCcSearches(prev => ({ ...prev, [idx]: e.target.value }))
                                  updateCorrectionLine(idx, 'cost_center_uuid', '')
                                  updateCorrectionLine(idx, 'cost_center_label', '')
                                }}
                                onFocus={() => setCcSearches(prev => ({ ...prev, [idx]: corr.cost_center_label || '' }))}
                              />
                              {ccSearchVal && !corr.cost_center_uuid && (
                                <div className="absolute z-10 top-full left-0 right-0 bg-white border rounded shadow-lg max-h-40 overflow-y-auto">
                                  {filtered.map(cc => (
                                    <button
                                      key={cc.id}
                                      className="block w-full text-left px-2 py-1 text-sm hover:bg-blue-50"
                                      onClick={() => {
                                        updateCorrectionLine(idx, 'cost_center_uuid', cc.id)
                                        updateCorrectionLine(idx, 'cost_center_label', `${cc.code} ${cc.name}`)
                                        setCcSearches(prev => ({ ...prev, [idx]: '' }))
                                      }}
                                    >
                                      <span className="font-mono">{cc.code}</span>{' '}
                                      <span className="text-gray-600">{cc.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <input
                              type="number" step="0.01"
                              className="w-24 border rounded px-2 py-1 text-right font-mono text-sm"
                              value={corr.amount_net}
                              onChange={(e) => updateCorrectionLine(idx, 'amount_net', e.target.value)}
                            />
                            <button className="text-red-400 hover:text-red-600 text-xs" onClick={() => removeCorrectionLine(idx)}>
                              &times;
                            </button>
                          </div>
                          <input
                            type="text"
                            className="w-full border rounded px-2 py-1 text-sm ml-6"
                            placeholder={t('correction.correctionReason')}
                            value={corr.reason}
                            onChange={(e) => updateCorrectionLine(idx, 'reason', e.target.value)}
                          />
                          {/* Dropped inspection entries */}
                          {droppedInspections.length > 0 && (
                            <div className="ml-6 space-y-1">
                              {droppedInspections.map(ie => {
                                const edited = editedInspections[ie.id]
                                const displayShare = edited?.customer_share || ie.customer_share
                                const isEdited = !!edited
                                return (
                                  <div key={ie.id} className="flex items-center gap-2 text-xs bg-blue-50 px-2 py-1 rounded">
                                    <span className="text-blue-600">&#x2514;</span>
                                    <span>{ie.name_last} {ie.name_first}</span>
                                    <span className={`font-mono ml-auto ${isEdited ? 'text-orange-600 font-semibold' : ''}`}>
                                      {formatCurrency(displayShare)} &euro;
                                      {isEdited && ' *'}
                                    </span>
                                    <span className="text-gray-400 truncate max-w-[120px]">{ie.address}</span>
                                    <button
                                      className="text-gray-300 hover:text-blue-500"
                                      onClick={() => setDetailEntry(ie)}
                                    >
                                      <Info className="w-3 h-3" />
                                    </button>
                                    <button
                                      className="text-gray-300 hover:text-orange-500"
                                      onClick={() => setEditingInspEntry(ie)}
                                      title={t('correction.editInspection')}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button
                                      className="text-red-400 hover:text-red-600"
                                      onClick={() => toggleInspectionOnLine(idx, ie.id)}
                                      title={t('correction.removeInspection')}
                                    >
                                      &times;
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                          {droppedInspections.length === 0 && availableInspections.length > 0 && (
                            <div className="ml-6 text-xs text-gray-300 italic py-1">
                              {t('correction.dropHere')}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    <button className="text-blue-600 hover:text-blue-800 text-sm" onClick={addCorrectionLine}>
                      + {t('correction.addLine')}
                    </button>
                  </div>

                  {/* Available inspection entries (drag source) */}
                  {unassignedInspections.length > 0 && (
                    <div className="border rounded p-3 bg-gray-50">
                      <p className="text-xs text-gray-500 mb-2">{t('correction.dragInspections')}:</p>
                      <div className="space-y-1">
                        {unassignedInspections.map(ie => (
                          <div
                            key={ie.id}
                            draggable
                            onMouseDown={() => window.getSelection()?.removeAllRanges()}
                            onDragStart={(e) => { e.dataTransfer.setData('text/plain', ie.id); e.dataTransfer.effectAllowed = 'move' }}
                            className="flex items-center gap-2 text-xs bg-white px-2 py-1.5 rounded border select-none cursor-grab active:cursor-grabbing hover:shadow-sm"
                          >
                            <span
                              className="text-gray-400 hover:text-gray-600 px-1 -mx-1 -my-1.5 py-1.5 cursor-grab active:cursor-grabbing"
                              title={t('correction.dragHandle')}
                            >&#x2630;</span>
                            <span>{ie.name_last} {ie.name_first}</span>
                            <span className="font-mono ml-auto">{formatCurrency(ie.customer_share)} &euro;</span>
                            <span className="text-gray-400 truncate max-w-[150px]">{ie.address}</span>
                            <button className="text-gray-300 hover:text-blue-500" onClick={() => setDetailEntry(ie)}>
                              <Info className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Correction total */}
                  <div className="flex justify-between text-sm font-semibold text-blue-800 pt-2">
                    <span>{t('correction.totalCorrection')}:</span>
                    <span className="font-mono">+{formatCurrency(String(corrections.reduce((s, c) => s + parseFloat(c.amount_net || '0'), 0)))} &euro;</span>
                  </div>
                </div>
              )}

              {/* Credit total (always visible in step 2) */}
              <div className="flex justify-between text-sm font-semibold text-red-800">
                <span>{t('correction.totalCredit')}:</span>
                <span className="font-mono">-{formatCurrency(String(totalCreditAmount))} &euro;</span>
              </div>

              {/* Net difference if credit and correction totals differ */}
              {correctionMode === 'credit_and_correction' && (() => {
                const corrTotal = corrections.reduce((s, c) => s + parseFloat(c.amount_net || '0'), 0)
                const diff = corrTotal - totalCreditAmount
                if (Math.abs(diff) > 0.005) {
                  return (
                    <div className={`flex justify-between text-sm font-semibold pt-1 border-t ${diff > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                      <span>{t('correction.netDifference')}:</span>
                      <span className="font-mono">{diff > 0 ? '+' : ''}{formatCurrency(String(diff))} &euro;</span>
                    </div>
                  )
                }
                return null
              })()}
            </div>
          )}

          {/* ========== STEP 3 ========== */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{t('correction.step3Title')}</p>

              {/* Credit summary — hidden in preInvoice mode */}
              {!preInvoice && (
                <div className="bg-red-50 rounded p-3">
                  <p className="text-sm font-semibold text-red-800 mb-1">{t('correction.creditLabel')}:</p>
                  {selectedEntries.map(ae => (
                    <div key={ae.id} className="text-sm text-red-700">
                      <div className="flex justify-between">
                        <span>{ae.cost_center} {ae.cost_center_description}</span>
                        <span className="font-mono">-{formatCurrency(creditAmounts[ae.id])} &euro;</span>
                      </div>
                      <div className="text-xs text-red-500 ml-4">&quot;{reason}&quot;</div>
                    </div>
                  ))}
                  <div className="flex justify-between mt-2 pt-2 border-t border-red-200 text-sm font-semibold text-red-800">
                    <span>{t('correction.totalCredit')}:</span>
                    <span className="font-mono">-{formatCurrency(String(totalCreditAmount))} &euro;</span>
                  </div>
                </div>
              )}

              {correctionMode === 'credit_and_correction' && corrections.length > 0 && (
                <div className="bg-blue-50 rounded p-3">
                  <p className="text-sm font-semibold text-blue-800 mb-1">{t('correction.correctionLabel')}:</p>
                  {corrections.map((corr, idx) => (
                    <div key={idx} className="text-sm text-blue-700">
                      <div className="flex justify-between">
                        <span>{corr.cost_center_label}</span>
                        <span className="font-mono">+{formatCurrency(corr.amount_net)} &euro;</span>
                      </div>
                      <div className="text-xs text-blue-500 ml-4">&quot;{corr.reason}&quot;</div>
                      {corr.copy_inspection_entry_ids.length > 0 && (
                        <div className="text-xs text-blue-400 ml-4">
                          {t('correction.copiedInspections', { count: corr.copy_inspection_entry_ids.length })}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-between mt-2 pt-2 border-t border-blue-200 text-sm font-semibold text-blue-800">
                    <span>{t('correction.totalCorrection')}:</span>
                    <span className="font-mono">+{formatCurrency(String(corrections.reduce((s, c) => s + parseFloat(c.amount_net || '0'), 0)))} &euro;</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">{t('correction.nextInvoiceNote')}</p>

              {/* Settlement adjustment */}
              <div className="border rounded p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiresSettlement}
                    onChange={(e) => setRequiresSettlement(e.target.checked)}
                  />
                  <span className="text-sm font-medium">{t('correction.requiresSettlement')}</span>
                </label>

                {requiresSettlement && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span>{t('correction.invoiceChange')}:</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(String(
                          -totalCreditAmount + (correctionMode === 'credit_and_correction'
                            ? corrections.reduce((s, c) => s + parseFloat(c.amount_net || '0'), 0)
                            : 0)
                        ))} &euro;
                      </span>
                      <span className="text-xs text-gray-400">({t('correction.autoCalculated')})</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <label>{t('correction.settlementChange')}:</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-28 border rounded px-2 py-1 text-right font-mono text-sm"
                        value={settlementChange}
                        onChange={(e) => setSettlementChange(e.target.value)}
                        placeholder="0.00"
                      />
                      <span className="text-xs text-gray-400">&euro;</span>
                    </div>
                    <div>
                      <label className="text-sm">{t('correction.settlementComments')}:</label>
                      <input
                        type="text"
                        className="w-full border rounded px-2 py-1 text-sm mt-1"
                        value={settlementComments}
                        onChange={(e) => setSettlementComments(e.target.value)}
                        placeholder={t('correction.settlementCommentsPlaceholder')}
                      />
                    </div>
                  </div>
                )}
              </div>

              {submitMutation.isError && (
                <div className="bg-red-100 text-red-700 text-sm rounded p-2">
                  {String((submitMutation.error as Error)?.message || t('correction.error'))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div>
            {step > (preInvoice ? 2 : 1) && (
              <button className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800" onClick={() => setStep(step - 1)}>
                <ChevronLeft className="w-4 h-4" />
                {t('correction.previous')}
              </button>
            )}
          </div>
          <div>
            {step < 3 && (
              <button
                className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
                onClick={() => setStep(step + 1)}
              >
                {t('correction.next')}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {step === 3 && (
              <button
                className="flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                disabled={submitMutation.isPending}
                onClick={handleSubmit}
              >
                <Check className="w-4 h-4" />
                {submitMutation.isPending ? t('correction.submitting') : t('correction.confirm')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Inspection detail modal */}
      {detailEntry && (
        <InspectionDetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} />
      )}

      {/* Inspection edit modal */}
      {editingInspEntry && (
        <InspectionEditModal
          entry={editingInspEntry}
          existingEdits={editedInspections[editingInspEntry.id]}
          onClose={() => setEditingInspEntry(null)}
          onSave={(edits) => {
            setEditedInspections(prev => ({ ...prev, [editingInspEntry.id]: edits }))
            // Update correction line amount_net if inspection shares changed
            setCorrections(prev => prev.map(c => {
              if (!c.copy_inspection_entry_ids.includes(editingInspEntry.id)) return c
              const newSum = c.copy_inspection_entry_ids.reduce((sum, iid) => {
                const edited = iid === editingInspEntry.id ? edits : editedInspections[iid]
                const share = edited ? parseFloat(edited.customer_share) : parseFloat(
                  availableInspections.find(ie => ie.id === iid)?.customer_share || '0'
                )
                return sum + share
              }, 0)
              return { ...c, amount_net: String(newSum) }
            }))
            setEditingInspEntry(null)
          }}
        />
      )}
    </div>
  )
}

// -- Known km prices --
const KNOWN_KM_PRICES = [1.79, 2.30]

// -- Known item types with Finnish names and split rules --
type ItemRule = 'per_passenger' | 'per_pct' | 'personal' | 'deduct'

const ITEM_TYPES: { key: string; type_id: number; label: string; rule: ItemRule }[] = [
  { key: 'Start_Fee', type_id: 1, label: 'Aloitusmaksu', rule: 'per_passenger' },
  { key: 'Route_Segment_Drive', type_id: 4, label: 'Reittiajo', rule: 'per_pct' },
  { key: 'Addition_Assistance', type_id: 5, label: 'Avustuslisä', rule: 'personal' },
  { key: 'Route_Stop_Wait', type_id: 3, label: 'Odotus', rule: 'per_pct' },
  { key: 'Waypoint', type_id: 8, label: 'Välipysähdys', rule: 'per_pct' },
  { key: 'Addition_Stretcher', type_id: 19, label: 'Paarilisä', rule: 'personal' },
  { key: 'Escort_Addition', type_id: 16, label: 'Saattolisä', rule: 'personal' },
  { key: 'Addition_Stairclimber', type_id: 6, label: 'Porrasvetolisä', rule: 'personal' },
  // Extra passenger's own payment share — entered as a positive amount that is
  // SUBTRACTED from this customer's share (the extra passenger pays it). Shown
  // in the "Lisämatkustaja" column (type_id 7+10) of the materials list.
  { key: 'Customer_Direct_Payment', type_id: 10, label: 'Lisämatkustaja', rule: 'deduct' },
]

function getItemLabel(type: string): string {
  return ITEM_TYPES.find(t => t.key === type)?.label || type
}

function getItemRule(type: string): ItemRule {
  return ITEM_TYPES.find(t => t.key === type)?.rule || 'per_pct'
}

// -- Inspection Edit Modal --
function InspectionEditModal({
  entry,
  existingEdits,
  onClose,
  onSave,
}: {
  entry: InspectionEntry
  existingEdits?: EditedInspectionData
  onClose: () => void
  onSave: (edits: EditedInspectionData) => void
}) {
  const { t } = useTranslation('billing')

  // distance_percentage is stored as fraction (0-1): 1.0 = 100%, 0.333 = 33.3%
  // Display as percentage (0-100)
  const rawPct = existingEdits?.distance_percentage ?? entry.distance_percentage ?? 1
  const [distancePct, setDistancePct] = useState(rawPct <= 1 ? rawPct * 100 : rawPct)

  // Derive the actual passenger count from the original share so recomputing
  // the existing items reproduces it. Combination per-passenger items (Start_Fee
  // etc.) are split by passenger count; a hardcoded "2" mis-split 3+ passenger
  // rides, shifting the base and making a Lisämatkustaja deduction look off.
  const [passengerCount, setPassengerCount] = useState(() => {
    if (!entry.is_combination) return 1
    const pctRaw = entry.distance_percentage ?? 1
    const pct = pctRaw <= 1 ? pctRaw : pctRaw / 100
    let perPax = 0
    let rest = 0
    for (const it of entry.items ?? []) {
      const amt = parseFloat(it.amount_net || '0')
      if (!amt) continue
      const rule = getItemRule(it.type)
      if (rule === 'per_passenger') perPax += amt
      else if (rule === 'per_pct') rest += amt * pct
      else if (rule === 'deduct') rest -= amt
      else rest += amt
    }
    const share = parseFloat(entry.customer_share || '0')
    const denom = share - rest
    if (perPax > 0 && denom > 0.005) return Math.max(1, Math.round(perPax / denom))
    return 2
  })
  const [distanceKm, setDistanceKm] = useState(
    existingEdits?.distance_meters != null
      ? (existingEdits.distance_meters / 1000).toFixed(2)
      : entry.distance_meters != null
        ? (entry.distance_meters / 1000).toFixed(2)
        : '0'
  )

  // Build items: start with existing data, then fill in missing known types with 0
  const existingItems = (existingEdits?.items ?? entry.items ?? []).map(item => {
    const knownType = ITEM_TYPES.find(t => t.key === item.type)
    return {
      type: item.type || '',
      type_id: (item.type_id && item.type_id !== 0) ? item.type_id : (knownType?.type_id ?? 0),
      amount_net: item.amount_net || '0',
    }
  })
  const existingKeys = new Set(existingItems.map(i => i.type))
  const allItems = [
    ...existingItems,
    ...ITEM_TYPES.filter(t => !existingKeys.has(t.key)).map(t => ({
      type: t.key, type_id: t.type_id, amount_net: '0',
    })),
  ]
  // Sort by ITEM_TYPES order
  const sortedInitial = allItems.sort((a, b) => {
    const ia = ITEM_TYPES.findIndex(t => t.key === a.type)
    const ib = ITEM_TYPES.findIndex(t => t.key === b.type)
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
  })
  const [items, setItems] = useState(sortedInitial)

  // Km price — derive from Route_Segment_Drive item
  const tripItemIdx = items.findIndex(i => i.type === 'Route_Segment_Drive')
  const derivedKmPrice = (() => {
    const km = parseFloat(distanceKm)
    const tripAmount = tripItemIdx >= 0 ? parseFloat(items[tripItemIdx].amount_net || '0') : 0
    if (km > 0 && tripAmount > 0) {
      const raw = tripAmount / km
      const closest = KNOWN_KM_PRICES.reduce((best, p) =>
        Math.abs(p - raw) < Math.abs(best - raw) ? p : best
      )
      return Math.abs(closest - raw) < 0.05 ? closest : raw
    }
    return KNOWN_KM_PRICES[0]
  })()
  const [kmPrice, setKmPrice] = useState(String(derivedKmPrice))

  // Calculate customer_share in real-time. Mirrors the backend: the per-passenger
  // and per-% splits apply ONLY to combination rides. On a non-combination ride
  // the customer pays the full amount of every item (distance_percentage is not
  // applied), so we sum items fully — except a 'deduct' item (Lisämatkustaja),
  // which always subtracts.
  const calculatedShare = useMemo(() => {
    const pct = distancePct / 100
    const passengers = Math.max(passengerCount, 1)
    const isComb = entry.is_combination
    let total = 0
    for (const item of items) {
      const amount = parseFloat(item.amount_net || '0')
      if (amount === 0) continue
      const rule = getItemRule(item.type)
      if (rule === 'deduct') total -= amount // extra passenger's own share
      else if (!isComb) total += amount // non-combination: full amount
      else if (rule === 'per_passenger') total += amount / passengers
      else if (rule === 'per_pct') total += amount * pct
      else total += amount // personal
    }
    return Math.round(total * 100) / 100
  }, [items, distancePct, passengerCount, entry.is_combination])

  // Update Route_Segment_Drive amount when km or price changes
  function handleKmChange(newKm: string) {
    setDistanceKm(newKm)
    if (tripItemIdx >= 0) {
      const newAmount = (parseFloat(newKm) * parseFloat(kmPrice)).toFixed(2)
      setItems(prev => prev.map((it, i) => i === tripItemIdx ? { ...it, amount_net: newAmount } : it))
    }
  }

  function handleKmPriceChange(newPrice: string) {
    setKmPrice(newPrice)
    if (tripItemIdx >= 0) {
      const newAmount = (parseFloat(distanceKm) * parseFloat(newPrice)).toFixed(2)
      setItems(prev => prev.map((it, i) => i === tripItemIdx ? { ...it, amount_net: newAmount } : it))
    }
  }

  function handleSave() {
    // Only include items with non-zero amounts
    const activeItems = items.filter(i => parseFloat(i.amount_net || '0') !== 0)
    onSave({
      customer_share: String(calculatedShare),
      distance_percentage: distancePct / 100, // Store back as fraction
      distance_meters: Math.round(parseFloat(distanceKm) * 1000),
      items: activeItems.map(i => ({ type: i.type, type_id: i.type_id, amount_net: i.amount_net })),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[550px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">{t('correction.editInspectionTitle')}: {entry.name_last} {entry.name_first}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Distance % and passengers */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('inspection.distancePercentage')}</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" step="0.1" min="0" max="100"
                  className="w-20 border rounded px-2 py-1 text-sm text-right font-mono"
                  value={distancePct} onChange={e => setDistancePct(parseFloat(e.target.value) || 0)}
                />
                <span className="text-sm">%</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('correction.passengers')}</label>
              <input
                type="number" step="1" min="1" max="10"
                className="w-16 border rounded px-2 py-1 text-sm text-right font-mono"
                value={passengerCount} onChange={e => setPassengerCount(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>

          {/* Km and price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('correction.kilometers')}</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" step="0.01" min="0"
                  className="w-24 border rounded px-2 py-1 text-sm text-right font-mono"
                  value={distanceKm} onChange={e => handleKmChange(e.target.value)}
                />
                <span className="text-sm">km</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('correction.kmPrice')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" step="0.0001" min="0"
                  className="w-24 border rounded px-2 py-1 text-sm text-right font-mono"
                  value={kmPrice} onChange={e => handleKmPriceChange(e.target.value)}
                />
                <span className="text-xs text-gray-400">&#8364;/km</span>
              </div>
              <div className="flex gap-2 mt-1">
                {KNOWN_KM_PRICES.map(p => (
                  <label key={p} className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="radio" name="kmPrice" checked={Math.abs(parseFloat(kmPrice) - p) < 0.001}
                      onChange={() => handleKmPriceChange(String(p))} className="w-3 h-3" />
                    {p.toFixed(4)}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t('correction.itemBreakdown')}</label>
            <div className="border rounded divide-y">
              {items.map((item, idx) => {
                const rule = getItemRule(item.type)
                const isComb = entry.is_combination
                const ruleLabel = rule === 'deduct' ? '−'
                  : !isComb ? '='
                  : rule === 'per_passenger' ? '÷matk'
                  : rule === 'per_pct' ? '×%'
                  : 'hlö'
                const amount = parseFloat(item.amount_net || '0')
                const share = rule === 'deduct'
                  ? -amount
                  : !isComb
                    ? amount
                    : rule === 'per_passenger'
                      ? amount / Math.max(passengerCount, 1)
                      : rule === 'per_pct'
                        ? amount * (distancePct / 100)
                        : amount
                const hasValue = amount !== 0
                return (
                  <div key={idx} className={`flex items-center gap-2 px-2 py-1.5 text-sm ${hasValue ? '' : 'opacity-40'}`}>
                    <span className="flex-1 text-sm">{getItemLabel(item.type)}</span>
                    <span className="text-xs text-gray-400 w-10 text-center">{ruleLabel}</span>
                    <input
                      type="number" step="0.01"
                      className="w-24 border rounded px-2 py-0.5 text-right font-mono text-sm"
                      value={item.amount_net}
                      onChange={e => setItems(prev => prev.map((it, i) => i === idx ? { ...it, amount_net: e.target.value } : it))}
                    />
                    <span className="text-xs text-gray-400 w-20 text-right font-mono">
                      {hasValue ? `= ${share.toFixed(2)} \u20ac` : ''}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Calculated share */}
          <div className="bg-gray-50 rounded p-3 flex justify-between items-center">
            <span className="text-sm font-medium">{t('inspection.customerShare')} ({t('correction.calculated')}):</span>
            <span className="text-lg font-mono font-semibold">{calculatedShare.toFixed(2)} &#8364;</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t">
          <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={onClose}>
            {t('correction.cancel')}
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={handleSave}
          >
            {t('correction.saveEdit')}
          </button>
        </div>
      </div>
    </div>
  )
}
